import { BareActionContext, getStoreBuilder } from 'vuex-typex';
import axios from 'axios';
import { RootState } from '@/store/state';
import { OrdersState } from './state';
import { Order, Result } from '@/models';
import appStore from '@/store/app';
import { SingleResultOf, Api, ApiResult, Guid, BaseProduct, TypeOfOperation, ResultOf } from '@/types';

type StoreContext = BareActionContext<OrdersState, RootState>;

const thisState: OrdersState = {
   ordersYear: undefined,
   orders: [],
   order: undefined,
   totalOrders: 0,
   cart: new Order(),
   cartLoaded: false,
   cartUpdate: false,
};

const moduleBuilder = getStoreBuilder<RootState>().module('orders', thisState);

const thisModule = {
   getters: {
      ordersYear(state: OrdersState): number | undefined {
         return state.ordersYear;
      },

      orders(state: OrdersState): Order[] {
         return state.orders;
      },

      order(state: OrdersState): Order | undefined {
         return state.order;
      },

      totalProducts(state: OrdersState): number {
         return state.totalOrders;
      },

      cart(state: OrdersState): Order {
         return state.cart;
      },

      isCartLoaded(state: OrdersState): boolean {
         return state.cartLoaded;
      },

      isCartUpdated(state: OrdersState): boolean {
         return state.cartUpdate;
      },

      cartCount(state: OrdersState): number {
         // return state.cart?.lines?.length ?? 0;
         return state.cart && state.cart.lines ? state.cart.lines.length : 0;
      },
   },

   actions: {
      async getCartAsync(context: StoreContext, payload: {}): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         return await axios.get<SingleResultOf<Order>>(Api.BaseUrl + `orders/cart`)
            .then((response) => {
               commitPopulateCart(response.data.payload || new Order());
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
                  if (reason.response.status === 404) {
                     commitSetCartAsLoaded();
                  }
               }
               commitPopulateCart(new Order());
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async addProductToCartAsync(context: StoreContext, payload: {
         product: BaseProduct,
         quantity: number,
         operation: TypeOfOperation,
         addToHistorical?: boolean
      }): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         return await axios.post<SingleResultOf<Order>>(Api.BaseUrl + `orders`,
            {
               type: payload.product.type,
               pharmacode: payload.product.pharmacode,
               quantity: payload.quantity,
               Operation: payload.operation,
               addToHistorical: payload.addToHistorical,
            })
            .then((response) => {
               if (response.data.payload) {
                  commitAddProductToCart(response.data.payload);
               }
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
                  if (reason.response.status === 404) {
                     commitSetCartAsLoaded();
                  }
               }
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async sendCartAsync(context: StoreContext, payload: { id: string }): Promise<boolean> {

         if (!payload) { return false; }

         appStore.loading({ isLoading: true });

         return await axios.post<Result>(Api.BaseUrl + `orders/send/${payload.id}`,
            {
               id: payload.id,
            })
            .then((response) => {
               if (response.data) {
                  appStore.success({ message: 'Ordine inviato con successo.' });
                  commitCartSubmitted();
               }
               return true;
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false });
               }
               return false;
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async syncOrderAsync(context: StoreContext, payload: { id: string }): Promise<boolean> {

         if (!payload) { return false; }

         appStore.loading({ isLoading: true });

         return await axios.post<Result>(Api.BaseUrl + `orders/sync/${payload.id}`,
            {
               id: payload.id,
            })
            .then((response) => {
               if (response.data) {
                  appStore.success({ message: 'Ordine sincronizzato con successo.' });
               }
               return true;
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false });
               }
               return false;
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async printOrderAsync(context: StoreContext, payload: { id: string }): Promise<Blob | undefined> {

         if (!payload || payload.id === Guid.Empty) {
            appStore.error({ message: 'Impossibile stampare. La richiesta ha un identificativo nullo.'});
            return;
         }

         appStore.loading({ isLoading: true });

         return await axios.get<Blob>(Api.BaseUrl + `orders/print/${payload.id}`, {
            responseType: 'blob',
         })
         .then((response) => {
            return response.data;
         })
         .catch((reason) => {
            if (reason && reason.response) {
               appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
            }
            return undefined;
         })
         .finally(() => {
            appStore.loading({ isLoading: false });
         });
      },

      async deleteProductInCartAsync(context: StoreContext, payload: { product: BaseProduct }): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         return await axios.delete(Api.BaseUrl + `orders/${payload.product.type}/${payload.product.pharmacode}`)
            .then((result) => {
               appStore.success({ message: 'Il prodotto è stato eliminato con successo.' });
               return commitDeleteProductInCart(payload.product);
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false });
               }
               return;
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async getOrdersAsync(context: StoreContext, payload: { year: number, pageNumber: number, pageSize: number }): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         return await axios.get<ResultOf<Order>>(Api.BaseUrl + `orders/${payload.year}`, {
            params: {
               page: payload.pageNumber,
               size: payload.pageSize,
            },
         })
            .then((response) => {
               commitPopulateOrders({ orders: response.data.payload, total: response.data.total });
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               commitPopulateOrders({ orders: [], total: 0 });
            })
            .finally(() => {
               commitPopulateOrdersYear({ year: payload.year });
               appStore.loading({ isLoading: false });
            });
      },

      async getOrderAsync(context: StoreContext, payload: { id: string }): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         return await axios.get<SingleResultOf<Order>>(Api.BaseUrl + `orders/${payload.id}`)
            .then((response) => {
               commitPopulateOrder(response.data.payload);
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false });
                  if (reason.response.status === 404) {
                     commitPopulateOrder(undefined);
                  }
               }
               commitPopulateCart(new Order());
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },
   },

   mutations: {

      populateOrdersYear(state: OrdersState, payload: { year: number | undefined }) {
         state.ordersYear = payload.year;
      },

      populateOrders(state: OrdersState, payload: { orders: Order[], total: number }) {
         state.orders = payload.orders;
         state.totalOrders = payload.total;
      },

      populateCart(state: OrdersState, payload: Order) {
         state.cart = payload;
         if (payload.id !== Guid.Empty) {
            state.cartLoaded = true;
            state.cartUpdate = false;
         }
      },


      populateOrder(state: OrdersState, payload: Order | undefined) {
         state.order = payload;
      },

      setCartAsLoaded(state: OrdersState) {
         state.cartLoaded = true;
         state.cartUpdate = false;
      },

      setCartAsSubmitted(state: OrdersState) {
         state.cartLoaded = false;
         state.cart = new Order();
      },

      addProductToCart(state: OrdersState, payload: Order) {

         if (!state.cart) {
            state.cart = new Order();
         }

         state.cart.id = payload.id;
         state.cart.documentDate = payload.documentDate;
         state.cart.documentNumber = payload.documentNumber;
         state.cart.documentReference = payload.documentReference;
         state.cart.status = payload.status;
         state.cart.statusDescription = payload.statusDescription;
         state.cart.transmissionDate = payload.transmissionDate;

         if (!state.cart.lines) {
            state.cart.lines = [];
         }

         // There's always just one line updated.
         if (payload.lines) {
            const lineUpdated = payload.lines[0];
            const index = state.cart.lines.findIndex((x) => x.productType === lineUpdated.productType && x.pharmacode === lineUpdated.pharmacode);
            if (index === -1) {
               state.cart.lines.push(lineUpdated);
            } else {
               state.cart.lines.splice(index, 1, lineUpdated);
            }
         }

         state.cartUpdate = true;
      },

      removeProductInCart(state: OrdersState, payload: BaseProduct) {

         if (!state.cart || !state.cart.lines) return;

         const index = state.cart.lines.findIndex((line) => line.productType === payload.type && line.pharmacode === payload.pharmacode);
         if (index > -1) {
            state.cart.lines.splice(index, 1);
         }

         state.cartUpdate = true;
      },
   },

};

// private getters
const getOrdersYear = moduleBuilder.read(thisModule.getters.ordersYear, 'getOrdersYear');
const getOrders = moduleBuilder.read(thisModule.getters.orders, 'getOrders');
const getOrder = moduleBuilder.read(thisModule.getters.order, 'getOrder');
const getTotalOrders = moduleBuilder.read(thisModule.getters.totalProducts, 'getTotalOrders');
const getCart = moduleBuilder.read(thisModule.getters.cart, 'getCart');
const getIsCartLoaded = moduleBuilder.read(thisModule.getters.isCartLoaded, 'getIsCartLoaded');
const getCartCount = moduleBuilder.read(thisModule.getters.cartCount, 'getCartCount');
const getCartIsUpdate = moduleBuilder.read(thisModule.getters.isCartUpdated, 'getCartIsUpdate');

// mutations
const commitPopulateOrdersYear = moduleBuilder.commit(thisModule.mutations.populateOrdersYear, 'commitPopulateOrdersYear');
const commitPopulateOrders = moduleBuilder.commit(thisModule.mutations.populateOrders, 'commitPopulateOrders');
const commitPopulateCart = moduleBuilder.commit(thisModule.mutations.populateCart, 'commitPopulateCart');
const commitAddProductToCart = moduleBuilder.commit(thisModule.mutations.addProductToCart, 'commitAddProductToCart');
const commitDeleteProductInCart = moduleBuilder.commit(thisModule.mutations.removeProductInCart, 'commitDeleteProductInCart');
const commitSetCartAsLoaded = moduleBuilder.commit(thisModule.mutations.setCartAsLoaded, 'commitSetCartAsLoaded');
const commitCartSubmitted = moduleBuilder.commit(thisModule.mutations.setCartAsSubmitted, 'commitCartSubmitted');
const commitPopulateOrder = moduleBuilder.commit(thisModule.mutations.populateOrder, 'commitPopulateOrder');

const orders = {

   // getters (wrapped as real getters)
   get ordersYear() { return getOrdersYear(); },
   get orders() { return getOrders(); },
   get order() { return getOrder(); },
   get totalOrders() { return getTotalOrders(); },
   get cart() { return getCart(); },
   get isCartLoaded() { return getIsCartLoaded(); },
   get cartCount() { return getCartCount(); },
   get isCartUpdate() { return getCartIsUpdate(); },

   // actions
   fetchCartAsync: moduleBuilder.dispatch(thisModule.actions.getCartAsync, 'getCartAsync'),
   addProductToCartAsync: moduleBuilder.dispatch(thisModule.actions.addProductToCartAsync, 'addProductToCartAsync'),
   deleteProductInCartAsync: moduleBuilder.dispatch(thisModule.actions.deleteProductInCartAsync, 'deleteProductInCartAsync'),
   sendCartAsync: moduleBuilder.dispatch(thisModule.actions.sendCartAsync, 'sendCartAsync'),
   fetchOrdersAsync: moduleBuilder.dispatch(thisModule.actions.getOrdersAsync, 'getOrdersAsync'),
   fetchOrderAsync: moduleBuilder.dispatch(thisModule.actions.getOrderAsync, 'getOrderAsync'),
   syncOrderAsync: moduleBuilder.dispatch(thisModule.actions.syncOrderAsync, 'syncOrderAsync'),
   printOrderAsync: moduleBuilder.dispatch(thisModule.actions.printOrderAsync, 'printOrderAsync'),
};

export default orders;
